import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { clientiService, type ClienteFinaleLink } from '@/services/clientiService';
import { queryKeys } from '@/lib/queryKeys';
import { usePermissions } from './usePermissions';
import type {
  AssegnaDocumentoInput,
  Cliente,
  ClienteFilters,
  ClienteFonte,
  ClienteFonteRevisione,
  ClienteFonteVisibilita,
  CreateClienteInput,
  CreateClienteFonteInput,
  FonteAssegnazione,
  Paginated,
  UUID,
  UpdateClienteInput,
  UpdateClienteFonteInput,
  VisibilitaCliente,
  VisibilitaWenkey,
} from '@/types';

export function useClienti(filters: ClienteFilters = {}): UseQueryResult<Paginated<Cliente>> {
  const { canManageClienti } = usePermissions();

  return useQuery({
    queryKey: queryKeys.clienti.list(filters),
    queryFn: () => clientiService.list(filters),
    // Tenant users would get an empty set from RLS; skip the request entirely.
    enabled: canManageClienti,
    meta: { resourceName: 'clienti' },
  });
}

export function useClientiFinali(partnerId: UUID | undefined): UseQueryResult<ClienteFinaleLink[]> {
  return useQuery({
    queryKey: queryKeys.clienti.clientiFinali(partnerId ?? ''),
    queryFn: () => clientiService.listClientiFinali(partnerId as UUID),
    enabled: Boolean(partnerId),
    meta: { resourceName: 'clienti finali' },
  });
}

export function useCreateClienteFinale(partnerId: UUID): UseMutationResult<Cliente, Error, CreateClienteInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => clientiService.createClienteFinale(partnerId, input),
    meta: { successMessage: 'Cliente finale creato.', silentError: true },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.clientiFinali(partnerId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.all });
    },
  });
}

export function useUpdateClienteFinale(
  partnerId: UUID,
): UseMutationResult<Cliente, Error, { finaleId: UUID; input: UpdateClienteInput }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ finaleId, input }) => clientiService.updateClienteFinale(partnerId, finaleId, input),
    meta: { successMessage: 'Cliente finale aggiornato.', silentError: true },
    onSuccess: (cliente) => {
      queryClient.setQueryData(queryKeys.clienti.detail(cliente.id), cliente);
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.clientiFinali(partnerId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.all });
    },
  });
}

export function useDeleteClienteFinale(partnerId: UUID): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (finaleId) => clientiService.removeClienteFinale(partnerId, finaleId),
    meta: { successMessage: 'Cliente finale eliminato.' },
    onSuccess: (_void, finaleId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.clientiFinali(partnerId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.detail(finaleId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.utenti.all });
    },
  });
}

export function useCliente(id: UUID | undefined): UseQueryResult<Cliente> {
  return useQuery({
    queryKey: queryKeys.clienti.detail(id ?? ''),
    queryFn: () => clientiService.get(id as UUID),
    enabled: Boolean(id),
  });
}

export interface ClienteSelectOption {
  value: UUID;
  label: string;
}

/**
 * Resolves a tenant already picked in the global switcher so async
 * autocompletes can keep showing it even when it is outside the current
 * search page.
 */
export function usePinnedClienteOption(id: UUID | undefined | null): ClienteSelectOption | undefined {
  const query = useCliente(id ?? undefined);
  if (!id) return undefined;
  return { value: id, label: query.data?.ragione_sociale ?? 'Cliente selezionato' };
}

export function withPinnedOption<T extends { value: string }>(options: T[], pinned: T | undefined): T[] {
  if (!pinned) return options;
  if (options.some((option) => option.value === pinned.value)) return options;
  return [pinned, ...options];
}

export function useCreateCliente(): UseMutationResult<Cliente, Error, CreateClienteInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateClienteInput) => clientiService.create(input),
    meta: { successMessage: 'Cliente creato.', silentError: true },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.contratti.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.adminSummary });
    },
  });
}

export function useUpdateCliente(): UseMutationResult<Cliente, Error, { id: UUID; input: UpdateClienteInput }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: UUID; input: UpdateClienteInput }) => clientiService.update(id, input),
    meta: { successMessage: 'Cliente aggiornato.', silentError: true },
    onSuccess: (cliente) => {
      queryClient.setQueryData(queryKeys.clienti.detail(cliente.id), cliente);
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.contratti.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.utenti.all });
    },
  });
}

/**
 * Suspend / reactivate. Invalidates the admin summary (active-client counters)
 * and utenti so tenant accounts show as Disattivato immediately.
 */
export function useSetClienteStato(): UseMutationResult<
  Cliente,
  Error,
  { id: UUID; action: 'suspend' | 'reactivate' | 'attiva'; motivo?: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, action }: { id: UUID; action: 'suspend' | 'reactivate' | 'attiva'; motivo?: string }) => {
      if (action === 'suspend') return clientiService.suspend(id);
      if (action === 'attiva') return clientiService.attiva(id);
      return clientiService.reactivate(id);
    },
    meta: { successMessage: 'Stato cliente aggiornato.' },
    onSuccess: (cliente) => {
      queryClient.setQueryData(queryKeys.clienti.detail(cliente.id), cliente);
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.adminSummary });
      void queryClient.invalidateQueries({ queryKey: queryKeys.utenti.all });
    },
  });
}

export function useDeleteCliente(): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => clientiService.remove(id),
    meta: { successMessage: 'Cliente spostato tra i persi. Resta ripristinabile per 6 mesi.' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.adminSummary });
      void queryClient.invalidateQueries({ queryKey: queryKeys.utenti.all });
    },
  });
}

export function useClientiPersi(filters: ClienteFilters = {}): UseQueryResult<Paginated<Cliente>> {
  const { canManageClienti } = usePermissions();
  return useQuery({
    queryKey: queryKeys.clienti.persi(filters),
    queryFn: () => clientiService.listPersi(filters),
    enabled: canManageClienti,
    meta: { resourceName: 'archivio clienti' },
  });
}

export function useRestoreCliente(): UseMutationResult<Cliente, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: UUID) => clientiService.restore(id),
    meta: { successMessage: 'Cliente ripristinato.' },
    onSuccess: (cliente) => {
      queryClient.setQueryData(queryKeys.clienti.detail(cliente.id), cliente);
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.adminSummary });
      void queryClient.invalidateQueries({ queryKey: queryKeys.utenti.all });
    },
  });
}

export function usePurgeCliente(): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: UUID) => clientiService.purge(id),
    meta: { successMessage: 'Cliente eliminato definitivamente.' },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: queryKeys.clienti.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.adminSummary });
      void queryClient.invalidateQueries({ queryKey: queryKeys.utenti.all });
    },
  });
}

function invalidateFonti(queryClient: ReturnType<typeof useQueryClient>, clienteId: UUID): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.fonti(clienteId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.fontiCestino(clienteId) });
  void queryClient.invalidateQueries({ queryKey: ['clienti', 'fonti'] });
}

export function useClienteFonti(clienteId: UUID | undefined): UseQueryResult<ClienteFonte[]> {
  const { canManageClienti, isStaff } = usePermissions();
  return useQuery({
    queryKey: queryKeys.clienti.fonti(clienteId ?? ''),
    queryFn: () => clientiService.listFonti(clienteId as UUID),
    // Internal documentation: staff only (never tenant portal).
    enabled: Boolean(clienteId) && (canManageClienti || isStaff),
    meta: { resourceName: 'documentazione cliente' },
  });
}

export function useCreateClienteFonte(
  clienteId: UUID,
): UseMutationResult<ClienteFonte, Error, CreateClienteFonteInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClienteFonteInput) => clientiService.createFonte(clienteId, input),
    meta: { successMessage: 'Fonte aggiunta.', silentError: true },
    onSuccess: () => invalidateFonti(queryClient, clienteId),
  });
}

export function useUpdateClienteFonte(
  clienteId: UUID,
): UseMutationResult<ClienteFonte, Error, { fonteId: UUID; input: UpdateClienteFonteInput }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fonteId, input }) => clientiService.updateFonte(clienteId, fonteId, input),
    meta: { successMessage: 'Fonte aggiornata.', silentError: true },
    onSuccess: () => invalidateFonti(queryClient, clienteId),
  });
}

export function useUploadClienteFonte(
  clienteId: UUID,
): UseMutationResult<ClienteFonte, Error, { file: File; titolo?: string; descrizione?: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, titolo, descrizione }) => clientiService.uploadFonte(clienteId, file, { titolo, descrizione }),
    meta: { successMessage: 'File caricato.', silentError: true },
    onSuccess: () => invalidateFonti(queryClient, clienteId),
  });
}

export function useDeleteClienteFonte(clienteId: UUID): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fonteId: UUID) => clientiService.removeFonte(clienteId, fonteId),
    meta: { successMessage: 'Spostata nel cestino per 90 giorni.' },
    onSuccess: () => invalidateFonti(queryClient, clienteId),
  });
}

export function useClienteFontiCestino(clienteId: UUID | undefined): UseQueryResult<ClienteFonte[]> {
  const { canManageClienti } = usePermissions();
  return useQuery({
    queryKey: queryKeys.clienti.fontiCestino(clienteId ?? ''),
    queryFn: () => clientiService.listCestino(clienteId as UUID),
    enabled: Boolean(clienteId) && canManageClienti,
    meta: { resourceName: 'cestino documentazione' },
  });
}

export function useRestoreClienteFonte(clienteId: UUID): UseMutationResult<ClienteFonte, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fonteId: UUID) => clientiService.restoreFonte(clienteId, fonteId),
    meta: { successMessage: 'Fonte ripristinata.' },
    onSuccess: () => invalidateFonti(queryClient, clienteId),
  });
}

export function usePurgeClienteFonte(clienteId: UUID): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fonteId: UUID) => clientiService.purgeFonte(clienteId, fonteId),
    meta: { successMessage: 'Fonte eliminata definitivamente.' },
    onSuccess: () => invalidateFonti(queryClient, clienteId),
  });
}

export function useClienteFonteRevisioni(
  clienteId: UUID,
  fonteId: UUID | undefined,
): UseQueryResult<ClienteFonteRevisione[]> {
  return useQuery({
    queryKey: queryKeys.clienti.fontiRevisioni(clienteId, fonteId ?? ''),
    queryFn: () => clientiService.listRevisioni(clienteId, fonteId as UUID),
    enabled: Boolean(fonteId),
    meta: { resourceName: 'storico fonte' },
  });
}

export function useRestoreClienteFonteRevisione(
  clienteId: UUID,
): UseMutationResult<ClienteFonte, Error, { fonteId: UUID; revId: UUID }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fonteId, revId }) => clientiService.restoreRevisione(clienteId, fonteId, revId),
    meta: { successMessage: 'Versione ripristinata.' },
    onSuccess: (_data, { fonteId }) => {
      invalidateFonti(queryClient, clienteId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.clienti.fontiRevisioni(clienteId, fonteId) });
    },
  });
}

export function useClienteFontiVisibilita(filters: {
  page?: number;
  pageSize?: number;
  search?: string;
  clienteId?: UUID;
}): UseQueryResult<Paginated<ClienteFonteVisibilita>> {
  const { isStaff } = usePermissions();
  return useQuery({
    queryKey: queryKeys.clienti.fontiVisibilita(filters),
    queryFn: () => clientiService.listFontiVisibilita(filters),
    // Internal documentation visibility matrix: staff only.
    enabled: isStaff,
    meta: { resourceName: 'visibilità documentazione' },
  });
}

export function useSetFonteVisibilitaWenkey(): UseMutationResult<
  ClienteFonte,
  Error,
  { clienteId: UUID; fonteId: UUID; visibilita: VisibilitaWenkey }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clienteId, fonteId, visibilita }) =>
      clientiService.setFonteVisibilitaWenkey(clienteId, fonteId, visibilita),
    meta: { silentError: true },
    onSuccess: (_data, { clienteId }) => invalidateFonti(queryClient, clienteId),
  });
}

export function useSetFonteVisibilitaCliente(): UseMutationResult<
  ClienteFonte,
  Error,
  { clienteId: UUID; fonteId: UUID; visibilita: VisibilitaCliente }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clienteId, fonteId, visibilita }) =>
      clientiService.setFonteVisibilitaCliente(clienteId, fonteId, visibilita),
    meta: { silentError: true },
    onSuccess: (_data, { clienteId }) => invalidateFonti(queryClient, clienteId),
  });
}

export function useFonteAssegnazioni(
  clienteId: UUID | undefined,
  fonteId: UUID | undefined,
): UseQueryResult<FonteAssegnazione[]> {
  return useQuery({
    queryKey: queryKeys.clienti.fontiAssegnazioni(clienteId ?? '', fonteId ?? ''),
    queryFn: () => clientiService.listFonteAssegnazioni(clienteId as UUID, fonteId as UUID),
    enabled: Boolean(clienteId && fonteId),
    meta: { resourceName: 'assegnazioni documentazione' },
  });
}

export function useAssegnaFonte(): UseMutationResult<
  FonteAssegnazione[],
  Error,
  { clienteId: UUID; fonteId: UUID; input: AssegnaDocumentoInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clienteId, fonteId, input }) =>
      clientiService.assegnaFonte(clienteId, fonteId, input.utente_ids, input.puo_scaricare ?? true),
    meta: { successMessage: 'Assegnazioni aggiornate.' },
    onSuccess: (_data, { clienteId, fonteId }) => {
      invalidateFonti(queryClient, clienteId);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.clienti.fontiAssegnazioni(clienteId, fonteId),
      });
    },
  });
}

export function useRevocaAssegnazioneFonte(): UseMutationResult<
  void,
  Error,
  { clienteId: UUID; fonteId: UUID; utenteId: UUID }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clienteId, fonteId, utenteId }) =>
      clientiService.revocaFonteAssegnazione(clienteId, fonteId, utenteId),
    meta: { successMessage: 'Assegnazione revocata.' },
    onSuccess: (_data, { clienteId, fonteId }) => {
      invalidateFonti(queryClient, clienteId);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.clienti.fontiAssegnazioni(clienteId, fonteId),
      });
    },
  });
}
